import { find, filter, findIndex, isEmpty, isNil, union, intersection } from 'lodash/fp';
import { customAlphabet } from 'nanoid';
import {
    condition,
    from,
    identifier,
    select,
    sql,
    join,
    alias,
    columnExpression,
    exclude,
    rename,
    where,
    exists,
    not,
    parens,
    and,
    rowCountColumn,
    cte,
    groups
} from 'app/lib/utils/sql_generator.js';
import { JoinType } from 'app/lib/constants';
import { Graph } from 'app/lib/utils/graph';
import { GENERIC_TRANSFORM_TYPE } from 'app/store/__generated__/types';

const TABLE_PLACEHOLDER_RE = /\{\{inputs\.(\d+)\.table\}\}/;
const COLUMN_PLACEHOLDER_RE = /\{\{inputs\.(\d+)\.columns\.(\d+)\}\}/;

const buildGraph = (sources, criteria) =>
    new Graph(
        sources.map((s) => s.id),
        criteria.flatMap((c) => [
            { from: c.left.id, to: c.right.id },
            { from: c.right.id, to: c.left.id }
        ])
    );

const getConnectedSourceIds = (sources, criteria) => {
    if (isEmpty(sources)) {
        return [];
    }

    const dependencies = buildGraph(sources, criteria);
    return [sources[0].id, ...dependencies.getDownstreamNodes(sources[0].id)];
};

export const filterUnusedSources = (sources, criteria) => {
    const connectedSourceIds = getConnectedSourceIds(sources, criteria);
    const isConnected = (source) => connectedSourceIds.includes(source.id);

    return sources.filter(isConnected);
};

export const getUnusedSourcesIds = (sources, criteria) => {
    const connectedSourceIds = getConnectedSourceIds(sources, criteria);
    const isDisconnected = (source) => !connectedSourceIds.includes(source.id);

    return sources.filter(isDisconnected).map((source) => source.id);
};

export const getUnusedCriteriaIds = (sources, criteria) => {
    const unusedSourcesIds = getUnusedSourcesIds(sources, criteria);
    return criteria
        .filter(
            ({ left, right }) =>
                unusedSourcesIds.includes(left.id) && unusedSourcesIds.includes(right.id)
        )
        .map((criteria) => criteria.id);
};

export const filterUnusedCriteria = (sources, criteria) => {
    const disconnectedCriteriaIds = getUnusedCriteriaIds(sources, criteria);
    return criteria.filter(({ id }) => !disconnectedCriteriaIds.includes(id));
};

export const addAlias = (source, _, sources) => {
    const dupes = filter({ name: source.name }, sources);
    const index = findIndex({ id: source.id }, dupes);

    return {
        ...source,
        alias: dupes.length > 1 ? `${source.name}_${index}` : undefined
    };
};

export const buildColumnList = (sources) =>
    sources.map((source) => {
        const renames = Object.keys(source.renames ?? {})
            .filter((name) => !(source.excludes ?? []).includes(name))
            .map((name) => alias(identifier(name), identifier(source.renames[name])));
        const excludes = (source.excludes ?? []).map((c) => identifier(c));

        return columnExpression(
            identifier(source.alias ?? source.name, '*'),
            exclude(excludes),
            rename(renames)
        );
    });

const fromClause = (source) => from(alias(identifier(source.name), identifier(source.alias)));

const joinClauses = (sources, criteria) =>
    sources.slice(1).map((source) => {
        const conditions = criteria
            .filter(({ right }) => right.id === source.id)
            .map(({ left, right }) => {
                const otherSource = find({ id: left.id }, sources);

                return condition(
                    identifier(otherSource.alias ?? otherSource.name, left.column),
                    '=',
                    identifier(source.alias ?? source.name, right.column)
                );
            });

        return join(
            JoinType[source.joinType],
            alias(identifier(source.name), identifier(source.alias)),
            ...conditions
        );
    });

export const buildPreviewSql = (sources, criteria) =>
    sql(select('*', rowCountColumn()), fromClause(sources[0]), joinClauses(sources, criteria));

const buildKeyColumnList = (source, criteria, qualified = true) =>
    criteria
        .filter((c) => c.left.id === source.id || c.right.id === source.id)
        .map((c) => {
            const column = c.left.id === source.id ? c.left.column : c.right.column;

            return qualified ? identifier(source.name, column) : identifier(column);
        });

const keysWhereClause = (source, criteria) => {
    const conditions = criteria
        .filter((c) => c.left.id === source.id || c.right.id === source.id)
        .map((c) => {
            const column = c.left.id === source.id ? c.left.column : c.right.column;

            return condition(
                identifier(`${source.name} OUTER`, column),
                '=',
                identifier(source.name, column)
            );
        });

    return where(and(...conditions));
};

export const buildKeysSql = (source, sources, criteria, matched = false) => {
    let whereClause = exists(
        parens(
            sql(
                select(1),
                fromClause(sources[0]),
                joinClauses(sources, criteria),
                keysWhereClause(source, criteria)
            )
        )
    );

    if (!matched) {
        whereClause = not(whereClause);
    }

    return sql(
        select(...buildKeyColumnList(source, criteria, false), rowCountColumn()),
        from(alias(identifier(source.name), identifier(`${source.name} OUTER`))),
        where(whereClause)
    );
};

const generateColumnName = () => {
    const prefix = customAlphabet('ABCDEFGHIJKLMNOPQRSTUVWXYZ', 1);
    const suffix = customAlphabet('ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890', 7);

    return `${prefix()}${suffix()}`;
};

export const buildMultiplicationSql = (source, sources, criteria) => {
    const id = generateColumnName();
    const sourcesWithReplacement = sources.map((s) => {
        if (s.name === source.name) {
            return { ...s, name: 'WITH_ID', alias: null };
        }

        return s;
    });

    return sql(
        cte(
            'WITH_ID',
            sql(select(alias('ROW_NUMBER() OVER(ORDER BY NULL)', id), '*'), fromClause(source)),
            'RESULTS',
            sql(
                select(id),
                sources[0].name === source.name ? from('WITH_ID') : fromClause(sources[0]),
                joinClauses(sourcesWithReplacement, criteria)
            ),
            'COUNTS',
            sql(select(id, alias('COUNT(*)', 'CNT')), from('RESULTS'), groups(1))
        ),
        select('CNT', alias('COUNT(*)', 'CNT2')),
        from('COUNTS'),
        groups(1)
    );
};

export const buildSql = (sources, criteria) =>
    sql(
        select(...buildColumnList(sources)),
        fromClause(sources[0]),
        joinClauses(sources, criteria)
    );

const placeholderGenerators = (inputs) => {
    const getTableIndex = (name) => findIndex({ table: name }, inputs);
    const getTablePlaceholder = (name) => `{{inputs.${getTableIndex(name)}.table}}`;

    const getColumnIndex = (tableIndex, name) => inputs[tableIndex].columns.indexOf(name);
    const getColumnPlaceholder = (table, column) => {
        const i = getTableIndex(table);
        const j = getColumnIndex(i, column);

        return `{{inputs.${i}.columns.${j}}}`;
    };

    return {
        getTablePlaceholder,
        getColumnPlaceholder
    };
};

const identifierGenerators = (inputs) => ({
    getUid: (placeholder) => {
        const index = placeholder.match(TABLE_PLACEHOLDER_RE)?.[1];

        return isNil(index) ? placeholder : inputs[index].uid;
    },

    getTable: (placeholder) => {
        const index = placeholder.match(TABLE_PLACEHOLDER_RE)?.[1];

        return isNil(index) ? placeholder : inputs[index].table;
    },

    getColumn: (placeholder) => {
        const [, tableIndex, columnIndex] = placeholder.match(COLUMN_PLACEHOLDER_RE);

        return isNil(tableIndex) || isNil(columnIndex)
            ? placeholder
            : inputs[tableIndex].columns[columnIndex];
    }
});

const reKey = (o, fn) =>
    Object.keys(o ?? {}).reduce((acc, name) => {
        acc[fn(name)] = o[name];

        return acc;
    }, {});

export const addSourcePlaceholders = (sources, inputs) => {
    const { getTablePlaceholder, getColumnPlaceholder } = placeholderGenerators(inputs);

    return sources.map((source) => ({
        ...source,
        name: getTablePlaceholder(source.name),
        excludes: (source.excludes ?? []).map((name) => getColumnPlaceholder(source.name, name)),
        renames: reKey(source.renames, (name) => getColumnPlaceholder(source.name, name))
    }));
};

export const addCriteriaPlaceholders = (sources, criteria, inputs) => {
    const { getColumnPlaceholder } = placeholderGenerators(inputs);
    const getSourceName = (id) => find({ id }, sources).name;

    return criteria.map((criterion) => ({
        id: criterion.id,
        left: {
            id: criterion.left.id,
            column: getColumnPlaceholder(getSourceName(criterion.left.id), criterion.left.column)
        },
        right: {
            id: criterion.right.id,
            column: getColumnPlaceholder(getSourceName(criterion.right.id), criterion.right.column)
        }
    }));
};

export const removeSourcePlaceholders = (sources, inputs) => {
    const { getUid, getTable, getColumn } = identifierGenerators(inputs);

    return sources.map((source) => ({
        ...source,
        uid: getUid(source.name),
        name: getTable(source.name),
        excludes: (source.excludes ?? []).map(getColumn),
        renames: reKey(source.renames, (placeholder) => getColumn(placeholder))
    }));
};

export const removeCriteriaPlaceholders = (criteria, inputs) => {
    const { getColumn } = identifierGenerators(inputs);

    return criteria.map((criterion) => ({
        id: criterion.id,
        left: {
            id: criterion.left.id,
            column: getColumn(criterion.left.column)
        },
        right: {
            id: criterion.right.id,
            column: getColumn(criterion.right.column)
        }
    }));
};

export const decorateSources = (sources, criteria) =>
    filterUnusedSources(sources, criteria).map(addAlias);

export const getOutputs = (sources, assets) => {
    const outputs = [];

    sources.forEach((source) => {
        const asset = find({ name: source.name }, assets);
        Object.keys(source.renames ?? {}).forEach((name) => {
            const alias = source.renames[name];
            const type = find({ name }, asset.schema).type;

            if (!source.excludes?.includes(name)) {
                outputs.push({ name: alias, type });
            }
        });
    });

    return outputs;
};

export const getInputs = (sources, criteria) => {
    const renamedColumns = (source) => Object.keys(source.renames ?? {});
    const droppedColumns = (source) => {
        const columns = new Set(renamedColumns(source));

        if (!isEmpty(source.excludes)) {
            source.excludes.forEach((name) => columns.add(name));
        }

        return [...columns];
    };
    const referencedColumns = (source) => {
        const columns = new Set(droppedColumns(source));

        criteria.forEach(({ left, right }) => {
            if (left.id === source.id) {
                columns.add(left.column);
            }

            if (right.id === source.id) {
                columns.add(right.column);
            }
        });

        return [...columns];
    };

    const inputs = sources.reduce((acc, source) => {
        let input = acc[source.name];

        if (input) {
            input.columns = union(input.columns, referencedColumns(source));
            input.dropped = intersection(input.dropped, droppedColumns(source));
        } else {
            input = {
                uid: source.uid,
                table: source.name,
                passThrough: true,
                columns: referencedColumns(source),
                dropped: droppedColumns(source)
            };
        }

        acc[source.name] = input;

        return acc;
    }, {});

    return Object.values(inputs);
};

export const buildPayload = (assets, rawSources, rawCriteria) => {
    const criteria = filterUnusedCriteria(rawSources, rawCriteria);
    const sources = decorateSources(rawSources, rawCriteria);
    const inputs = getInputs(sources, criteria);
    const outputs = getOutputs(sources, assets);
    const sourcesWithPlaceholders = addSourcePlaceholders(sources, inputs);
    const criteriaWithPlaceholders = addCriteriaPlaceholders(sources, criteria, inputs);
    const sqlTemplate = buildSql(sourcesWithPlaceholders, criteriaWithPlaceholders);

    return {
        _type: GENERIC_TRANSFORM_TYPE,
        type: 'MULTI_JOIN',
        model: {
            inputs,
            outputs,
            sources: sourcesWithPlaceholders.map((source) => ({
                id: source.id,
                uid: source.uid,
                name: source.name,
                alias: source.alias,
                joinType: source.joinType,
                excludes: source.excludes,
                renames: source.renames
            })),
            criteria: criteriaWithPlaceholders
        },
        sqlTemplate
    };
};

export const getColumns = (sources, assets) => {
    const columns = [];

    sources.forEach((source) => {
        const asset = find({ name: source.name }, assets);

        asset.schema.forEach(({ name }) => {
            if (!source.excludes?.includes(name)) {
                columns.push(source.renames?.[name] ?? name);
            }
        });
    });

    return columns;
};

export const getRenames = (newColumns, allColumns) => {
    const renames = {};

    newColumns.forEach((column) => {
        let name = column;
        let i = 1;

        while (allColumns.includes(name)) {
            name = `${column}_${i++}`;
        }

        if (name !== column) {
            renames[column] = name;
        }
    });

    return renames;
};
